import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import TagEditor from '../Widgets/TagEditor';
import { useWebSocket } from '../WebSocketContext';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import { buildGetExportSettingsPayload } from '../../Utils/actionSettingsUtils';

const FIELD_TYPES = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  NUMBER: 'number',
  PASSWORD: 'password',
  SELECT: 'select',
  CHECKBOX: 'checkbox',
  JSON: 'json',
  TAG: 'tag',
  TAG_EDITOR: 'tag_editor',
};

const normalizeFieldType = (rawType) => String(rawType || FIELD_TYPES.TEXT).trim().toLowerCase().replace(/[\s-]+/g, '_');

const TAG_EDITOR_FIELD_TYPES = new Set([
  FIELD_TYPES.TEXT,
  FIELD_TYPES.TEXTAREA,
  FIELD_TYPES.NUMBER,
  FIELD_TYPES.JSON,
  FIELD_TYPES.TAG,
  FIELD_TYPES.TAG_EDITOR,
  'tageditor',
]);

const PATH_FIELD_TYPES = new Set([
  'path',
  'file',
  'filepath',
  'folder',
  'folderpath',
  'directory',
  'dirpath',
]);

const shouldUseTagEditor = (field, fieldType) => {
  if (!field) {
    return false;
  }
  if (field.useTagEditor === false) {
    return false;
  }
  if (field.useTagEditor === true) {
    return true;
  }
  return TAG_EDITOR_FIELD_TYPES.has(fieldType);
};

const isPathField = (field, fieldType) => {
  if (!field || typeof field !== 'object') {
    return false;
  }
  if (field.pathField === true || field.isPath === true) {
    return true;
  }
  if (PATH_FIELD_TYPES.has(fieldType)) {
    return true;
  }
  const key = String(field.key || '').trim().toLowerCase();
  if (!key) {
    return false;
  }
  return /(path|folder|file|target|output|source|template)/.test(key);
};

const normalizeOptions = (options) => {
  if (!Array.isArray(options)) {
    return [];
  }
  return options
    .map((option) => {
      if (typeof option === 'string') {
        return { value: option, label: option };
      }
      if (option && typeof option === 'object') {
        return {
          value: option.value ?? option.label ?? '',
          label: option.label ?? option.value ?? '',
        };
      }
      return null;
    })
    .filter(Boolean);
};

const buildDefaultValues = (fields) => {
  const defaults = {};
  fields.forEach((field) => {
    const fieldType = normalizeFieldType(field?.type);
    if (field.default !== undefined) {
      defaults[field.key] = field.default;
      return;
    }
    if (fieldType === FIELD_TYPES.CHECKBOX) {
      defaults[field.key] = false;
      return;
    }
    defaults[field.key] = '';
  });
  return defaults;
};

const normalizeForSave = (fields, values) => {
  const payload = {};
  fields.forEach((field) => {
    const key = field.key;
    if (!key) {
      return;
    }
    const fieldType = normalizeFieldType(field.type);
    const rawValue = values[key];

    if (fieldType === FIELD_TYPES.CHECKBOX) {
      payload[key] = Boolean(rawValue);
      return;
    }

    if (fieldType === FIELD_TYPES.NUMBER) {
      if (rawValue === '' || rawValue === null || rawValue === undefined) {
        payload[key] = '';
        return;
      }
      const numericValue = Number(rawValue);
      payload[key] = Number.isFinite(numericValue) ? numericValue : '';
      return;
    }

    if (fieldType === FIELD_TYPES.JSON) {
      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        if (!trimmed) {
          payload[key] = '';
          return;
        }
        try {
          payload[key] = JSON.parse(trimmed);
          return;
        } catch (error) {
          payload[key] = rawValue;
          return;
        }
      }
    }

    payload[key] = rawValue ?? '';
  });
  return payload;
};

const serializeForForm = (fields, customData, defaults) => {
  const next = { ...defaults };
  fields.forEach((field) => {
    const key = field.key;
    if (!key || customData?.[key] === undefined) {
      return;
    }
    const fieldType = normalizeFieldType(field.type);
    const rawValue = customData[key];

    if (fieldType === FIELD_TYPES.JSON && typeof rawValue !== 'string') {
      try {
        next[key] = JSON.stringify(rawValue, null, 2);
      } catch (error) {
        next[key] = String(rawValue ?? '');
      }
      return;
    }
    next[key] = rawValue;
  });
  return next;
};

const compareValues = (left, right) => {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch (error) {
    return false;
  }
};

const GENERIC_PLUGIN_OAUTH_COMMANDS = {
  authorize: 'authorizePluginActionOAuth',
  authorizeStart: 'authorizePluginActionOAuthStart',
  verify: 'verifyPluginActionOAuthToken',
  verifyResult: 'verifyPluginActionOAuthTokenResult',
  tokenResult: 'pluginActionOAuthTokenResult',
};

const OAUTH_PROVIDER_CONFIG = {
  outlook: {
    label: 'Outlook',
    authKey: 'outlookAuth',
    accountRequired: true,
    commands: {
      authorize: 'authorizeOutlookAction',
      authorizeStart: 'authorizeOutlookActionStart',
      verify: 'verifyOutlookActionToken',
      verifyResult: 'verifyOutlookActionTokenResult',
      tokenResult: 'outlookActionTokenResult',
    },
  },
  gmail: {
    label: 'Gmail',
    authKey: 'gmailAuth',
    accountRequired: true,
    commands: {
      authorize: 'authorizeGmailAction',
      authorizeStart: 'authorizeGmailActionStart',
      verify: 'verifyGmailActionToken',
      verifyResult: 'verifyGmailActionTokenResult',
      tokenResult: 'gmailActionTokenResult',
    },
  },
  teams: {
    label: 'Teams',
    authKey: 'teamsAuth',
    accountRequired: false,
    commands: {
      authorize: 'authorizeTeamsAction',
      authorizeStart: 'authorizeTeamsActionStart',
      verify: 'verifyTeamsActionToken',
      verifyResult: 'verifyTeamsActionTokenResult',
      tokenResult: 'teamsActionTokenResult',
    },
  },
  linkedin: {
    label: 'LinkedIn',
    authKey: 'linkedinAuth',
    accountRequired: true,
    commands: {
      authorize: 'authorizeLinkedinAction',
      authorizeStart: 'authorizeLinkedinActionStart',
      verify: 'verifyLinkedinActionToken',
      verifyResult: 'verifyLinkedinActionTokenResult',
      tokenResult: 'linkedinActionTokenResult',
    },
  },
};

const coerceBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) {
      return true;
    }
    if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) {
      return false;
    }
  }
  return fallback;
};

const normalizeOAuthConfig = (rawConfig) => {
  if (!rawConfig || typeof rawConfig !== 'object') {
    return null;
  }

  const providerKey = String(rawConfig.provider || '').trim().toLowerCase();
  if (!providerKey) {
    return null;
  }

  const providerDefinition = OAUTH_PROVIDER_CONFIG[providerKey];
  const commands = providerDefinition?.commands || GENERIC_PLUGIN_OAUTH_COMMANDS;

  const authKey = String(rawConfig.authKey || providerDefinition?.authKey || `${providerKey}Auth`).trim();
  const accountField = typeof rawConfig.accountField === 'string' && rawConfig.accountField.trim()
    ? rawConfig.accountField.trim()
    : null;

  return {
    provider: providerKey,
    label: rawConfig.label || providerDefinition?.label || providerKey,
    authKey: authKey || `${providerKey}Auth`,
    accountField,
    accountRequired: coerceBoolean(rawConfig.accountRequired, providerDefinition?.accountRequired ?? true),
    helperText: typeof rawConfig.helperText === 'string' ? rawConfig.helperText : '',
    connectLabel: typeof rawConfig.connectLabel === 'string' && rawConfig.connectLabel.trim()
      ? rawConfig.connectLabel.trim()
      : '',
    disconnectLabel: typeof rawConfig.disconnectLabel === 'string' && rawConfig.disconnectLabel.trim()
      ? rawConfig.disconnectLabel.trim()
      : '',
    commands,
  };
};

const ActionsPluginGeneric = ({ caseid, tags, settingId, onSaveStatusChange, pluginAction, initialScope }) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages, serverInfo: wsServerInfo } = useWebSocket();

  const fields = useMemo(() => {
    const rawFields = pluginAction?.settings?.fields;
    if (!Array.isArray(rawFields)) {
      return [];
    }
    return rawFields.filter((field) => field && typeof field === 'object' && field.key);
  }, [pluginAction]);
  const oauthConfig = useMemo(() => normalizeOAuthConfig(pluginAction?.oauth), [pluginAction]);
  const pluginLoadFailed = pluginAction?.loadFailed === true;
  const pluginLoadError = typeof pluginAction?.loadError === 'string' ? pluginAction.loadError.trim() : '';
  const pluginLogsPath = typeof pluginAction?.logsPath === 'string' ? pluginAction.logsPath.trim() : '';
  const pluginGuideUrl = typeof pluginAction?.guideUrl === 'string' ? pluginAction.guideUrl.trim() : '';
  const resolvedPluginGuideUrl = useMemo(() => {
    if (!pluginGuideUrl) {
      return '';
    }
    if (/^https?:\/\//i.test(pluginGuideUrl)) {
      return pluginGuideUrl;
    }
    if (!pluginGuideUrl.startsWith('/')) {
      return pluginGuideUrl;
    }
    const protocol = String(wsServerInfo?.webServerScheme || window.location.protocol.replace(':', '') || 'http');
    const hostname = String(window.location.hostname || 'localhost');
    const backendPort = Number(wsServerInfo?.webServerPort);
    if (Number.isInteger(backendPort) && backendPort > 0) {
      return `${protocol}://${hostname}:${backendPort}${pluginGuideUrl}`;
    }
    return `${window.location.origin}${pluginGuideUrl}`;
  }, [pluginGuideUrl, wsServerInfo]);

  const enableConditions = pluginAction?.settings?.enableConditions !== false;
  const defaultValues = useMemo(() => buildDefaultValues(fields), [fields]);
  const [formValues, setFormValues] = useState(defaultValues);
  const [oauthAccount, setOauthAccount] = useState('');
  const [oauthVerified, setOauthVerified] = useState(false);
  const [oauthPendingDisconnect, setOauthPendingDisconnect] = useState(false);
  const [oauthConnectionStatus, setOauthConnectionStatus] = useState('disconnected');
  const [oauthMessage, setOauthMessage] = useState('');
  const [oauthIsAuthorizing, setOauthIsAuthorizing] = useState(false);
  const [oauthIsChecking, setOauthIsChecking] = useState(false);
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);
  const [activePathFieldKey, setActivePathFieldKey] = useState('');
  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);
  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    ws,
    onFolderSelected: (selectedPath) => {
      if (!activePathFieldKey || !selectedPath) {
        return;
      }
      setFormValues((prev) => ({
        ...prev,
        [activePathFieldKey]: selectedPath,
      }));
      setActivePathFieldKey('');
    },
    onFolderBrowserClose: () => {
      setActivePathFieldKey('');
    },
  });
  const hasOAuthAccountField = useMemo(() => {
    if (!oauthConfig?.accountField) {
      return false;
    }
    return fields.some((field) => field.key === oauthConfig.accountField);
  }, [oauthConfig, fields]);

  useEffect(() => {
    setFormValues(defaultValues);
  }, [defaultValues]);

  const handleFieldChange = useCallback((key, value) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  useEffect(() => {
    if (!oauthConfig) {
      setOauthAccount('');
      setOauthVerified(false);
      setOauthPendingDisconnect(false);
      setOauthConnectionStatus('disconnected');
      setOauthMessage('');
      setOauthIsAuthorizing(false);
      setOauthIsChecking(false);
    }
  }, [oauthConfig]);

  const oauthAccountValue = useMemo(() => {
    if (!oauthConfig) {
      return '';
    }
    if (oauthConfig.accountField && hasOAuthAccountField) {
      const fieldValue = formValues[oauthConfig.accountField];
      if (typeof fieldValue === 'string') {
        return fieldValue.trim();
      }
      if (fieldValue === undefined || fieldValue === null) {
        return '';
      }
      return String(fieldValue).trim();
    }
    return oauthAccount.trim();
  }, [oauthConfig, hasOAuthAccountField, formValues, oauthAccount]);

  const customComparison = useCallback((initial) => {
    const initialFormValues = serializeForForm(fields, initial, defaultValues);
    const left = normalizeForSave(fields, formValues);
    const right = normalizeForSave(fields, initialFormValues);
    if (oauthConfig) {
      const initialAuth = initial?.[oauthConfig.authKey] || {};
      const rightAccount = oauthConfig.accountField && hasOAuthAccountField
        ? (right[oauthConfig.accountField] ?? initialAuth.account ?? '')
        : (initialAuth.account ?? '');
      const leftAccount = oauthConfig.accountField && hasOAuthAccountField
        ? (left[oauthConfig.accountField] ?? '')
        : oauthAccount;

      left[oauthConfig.authKey] = {
        account: String(leftAccount ?? '').trim(),
        verified: Boolean(oauthVerified),
        ...(oauthPendingDisconnect ? { disconnect: true } : {}),
      };
      right[oauthConfig.authKey] = {
        account: String(rightAccount ?? '').trim(),
        verified: Boolean(initialAuth.verified),
      };
    }
    return compareValues(left, right);
  }, [fields, defaultValues, formValues, oauthConfig, hasOAuthAccountField, oauthAccount, oauthVerified, oauthPendingDisconnect]);

  const customSaveData = useCallback(() => {
    const payload = normalizeForSave(fields, formValues);
    if (oauthConfig) {
      const account = oauthConfig.accountField && hasOAuthAccountField
        ? String(payload[oauthConfig.accountField] ?? '').trim()
        : oauthAccount.trim();
      payload[oauthConfig.authKey] = {
        account,
        verified: Boolean(oauthVerified),
        ...(oauthPendingDisconnect ? { disconnect: true } : {}),
      };
    }
    return payload;
  }, [fields, formValues, oauthConfig, hasOAuthAccountField, oauthAccount, oauthVerified, oauthPendingDisconnect]);

  const loadDataFunction = useCallback((settingsPayload) => {
    const customData = settingsPayload?.customData || {};
    const nextValues = serializeForForm(fields, customData, defaultValues);
    if (oauthConfig?.accountField && hasOAuthAccountField) {
      const authRecord = customData?.[oauthConfig.authKey] || {};
      if (!nextValues[oauthConfig.accountField] && authRecord?.account) {
        nextValues[oauthConfig.accountField] = authRecord.account;
      }
    }
    setFormValues(nextValues);

    if (oauthConfig) {
      const authRecord = customData?.[oauthConfig.authKey] || {};
      const accountFromAuth = typeof authRecord.account === 'string' ? authRecord.account : '';
      setOauthAccount(accountFromAuth);
      setOauthVerified(Boolean(authRecord.verified));
      setOauthPendingDisconnect(false);
      setOauthIsAuthorizing(false);
      setOauthIsChecking(false);

      if (authRecord.pendingState) {
        setOauthConnectionStatus('pending');
        setOauthMessage(t('workflow.plugin.oauth.pending', 'Complete the authentication flow in the opened tab.'));
      } else if (authRecord.verified) {
        setOauthConnectionStatus('connected');
        setOauthMessage(t('workflow.plugin.oauth.connected', 'Connected.'));
      } else {
        setOauthConnectionStatus('disconnected');
        setOauthMessage('');
      }
    }
  }, [fields, defaultValues, oauthConfig, hasOAuthAccountField, t]);

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    id,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: pluginAction?.type || '',
    onSaveStatusChange,
    customComparison,
    customSaveData,
    loadDataFunction,
    enableConditions,
    initialScope,
  });

  const isPersistedAction = useMemo(() => (
    id && !(typeof id === 'string' && id.startsWith('temp_'))
  ), [id]);

  const oauthAlertSeverity = oauthConnectionStatus === 'connected'
    ? 'success'
    : oauthConnectionStatus === 'disconnected'
      ? 'error'
      : 'info';

  const oauthStatusLabel = oauthConnectionStatus === 'connected'
    ? t('workflow.plugin.oauth.status_connected', 'Connected')
    : oauthConnectionStatus === 'pending'
      ? t('workflow.plugin.oauth.status_pending', 'Pending')
      : t('workflow.plugin.oauth.status_disconnected', 'Disconnected');

  const handleOAuthConnect = useCallback(() => {
    if (!oauthConfig || !ws) {
      return;
    }

    if (!isPersistedAction) {
      setOauthMessage(t('workflow.plugin.oauth.save_before_connect', 'Save the action before connecting.'));
      return;
    }

    if (oauthConfig.accountRequired && !oauthAccountValue) {
      setOauthMessage(t('workflow.plugin.oauth.account_required', 'Enter an account before connecting.'));
      return;
    }

    setOauthIsAuthorizing(true);
    setOauthMessage('');
    setOauthPendingDisconnect(false);

    let returnUrl = window.location.href;
    try {
      const url = new URL(returnUrl);
      url.searchParams.set('openActions', 'true');
      url.searchParams.set('exportId', String(id));
      returnUrl = url.toString();
    } catch (error) {
      // Keep current URL as fallback.
    }

    ws.send(JSON.stringify({
      command: oauthConfig.commands.authorize,
      caseid,
      exportId: id,
      account: oauthAccountValue,
      ...(oauthConfig.provider ? { provider: oauthConfig.provider } : {}),
      returnUrl,
    }));
  }, [oauthConfig, ws, isPersistedAction, oauthAccountValue, t, id, caseid]);

  const handleOAuthDisconnect = useCallback(() => {
    if (!oauthConfig) {
      return;
    }
    setOauthConnectionStatus('disconnected');
    setOauthMessage(t('workflow.plugin.oauth.disconnect_pending', 'Connection will be removed when you save.'));
    setOauthPendingDisconnect(true);
    setOauthVerified(false);
  }, [oauthConfig, t]);

  const handleOpenPluginGuide = useCallback(() => {
    if (!resolvedPluginGuideUrl) {
      return;
    }
    setIsGuideDialogOpen(true);
  }, [resolvedPluginGuideUrl]);

  const handleClosePluginGuide = useCallback(() => {
    setIsGuideDialogOpen(false);
  }, []);

  useEffect(() => {
    if (!oauthConfig || !subscribeToMessages) {
      return undefined;
    }

    const unsubscribe = subscribeToMessages((message) => {
      if (!message) {
        return;
      }

      if (message.command === oauthConfig.commands.authorizeStart) {
        const payload = message.data || {};
        if (payload.error) {
          setOauthMessage(payload.error);
          setOauthIsAuthorizing(false);
          return;
        }
        if (payload.exportId === id) {
          if (payload.url) {
            window.location.assign(payload.url);
          }
          setOauthConnectionStatus('pending');
          setOauthMessage(t('workflow.plugin.oauth.pending', 'Complete the authentication flow in the opened tab.'));
          setOauthIsAuthorizing(false);
        }
      } else if (message.command === oauthConfig.commands.tokenResult && message.data?.exportId === id) {
        const payload = message.data || {};
        setOauthIsAuthorizing(false);
        if (payload.verified) {
          setOauthConnectionStatus('connected');
          setOauthMessage(t('workflow.plugin.oauth.connected', 'Connected.'));
          setOauthVerified(true);
          setOauthPendingDisconnect(false);
          if (ws && isPersistedAction) {
            ws.send(JSON.stringify(buildGetExportSettingsPayload(id, caseid)));
          }
        } else {
          setOauthConnectionStatus('disconnected');
          setOauthMessage(payload.error || t('workflow.plugin.oauth.failed', 'Authentication failed.'));
          setOauthVerified(false);
        }
      } else if (message.command === oauthConfig.commands.verifyResult && message.data?.exportId === id) {
        const payload = message.data || {};
        setOauthIsChecking(false);
        if (payload.verified) {
          setOauthConnectionStatus('connected');
          setOauthMessage(t('workflow.plugin.oauth.connected', 'Connected.'));
          setOauthVerified(true);
          setOauthPendingDisconnect(false);
        } else {
          setOauthConnectionStatus('disconnected');
          setOauthMessage(payload.error || t('workflow.plugin.oauth.invalid', 'Connection requires re-authentication.'));
          setOauthVerified(false);
        }
      }
    });

    return unsubscribe;
  }, [oauthConfig, subscribeToMessages, id, t, ws, isPersistedAction, caseid]);

  useEffect(() => {
    if (!oauthConfig || !ws || !isPersistedAction) {
      return;
    }

    setOauthIsChecking(true);
    setOauthConnectionStatus('pending');
    setOauthMessage(t('workflow.plugin.oauth.checking', 'Checking connection status...'));
    ws.send(JSON.stringify({
      command: oauthConfig.commands.verify,
      caseid,
      exportId: id,
      ...(oauthConfig.provider ? { provider: oauthConfig.provider } : {}),
    }));
  }, [oauthConfig, ws, isPersistedAction, t, caseid, id]);

  const renderField = (field) => {
    const fieldType = normalizeFieldType(field.type);
    const key = field.key;
    const label = field.label || key;
    const helperText = field.helperText || field.description || '';
    const placeholder = field.placeholder || '';
    const required = Boolean(field.required);
    const value = formValues[key];
    const pathField = isPathField(field, fieldType);

    if (fieldType === FIELD_TYPES.CHECKBOX) {
      return (
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(event) => handleFieldChange(key, event.target.checked)}
            />
          }
          label={label}
        />
      );
    }

    if (fieldType === FIELD_TYPES.SELECT) {
      const options = normalizeOptions(field.options);
      return (
        <TextField
          fullWidth
          select
          label={label}
          value={value ?? ''}
          onChange={(event) => handleFieldChange(key, event.target.value)}
          helperText={helperText}
          required={required}
          size="small"
        >
          {options.map((option) => (
            <MenuItem key={`${key}_${option.value}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (pathField) {
      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
            {label}{required ? ' *' : ''}
          </Typography>
          <TagEditor
            caseid={caseid}
            value={typeof value === 'string' ? value : String(value ?? '')}
            onChange={(nextValue) => handleFieldChange(key, nextValue)}
            placeholder={placeholder}
            tags={tags}
            scope={initialScope}
            singleLine
            multiline={false}
          />
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setActivePathFieldKey(key);
                handleBrowseDirectory();
              }}
            >
              {t('common.browse', 'Browse')}
            </Button>
          </Box>
          {helperText && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              {helperText}
            </Typography>
          )}
        </Box>
      );
    }

    if (shouldUseTagEditor(field, fieldType)) {
      const isSingleLine = field.singleLine === true
        || field.multiline === false
        || fieldType === FIELD_TYPES.TAG
        || fieldType === FIELD_TYPES.TEXT
        || fieldType === FIELD_TYPES.NUMBER;
      const minRows = Number(field.rows || 4);
      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
            {label}{required ? ' *' : ''}
          </Typography>
          <TagEditor
            caseid={caseid}
            value={typeof value === 'string' ? value : String(value ?? '')}
            onChange={(nextValue) => handleFieldChange(key, nextValue)}
            placeholder={placeholder}
            tags={tags}
            scope={initialScope}
            singleLine={isSingleLine}
            multiline={!isSingleLine}
            minRows={!isSingleLine ? minRows : undefined}
          />
          {helperText && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              {helperText}
            </Typography>
          )}
        </Box>
      );
    }

    const multiline = fieldType === FIELD_TYPES.TEXTAREA || fieldType === FIELD_TYPES.JSON;
    const rows = multiline ? Number(field.rows || (fieldType === FIELD_TYPES.JSON ? 8 : 4)) : 1;
    const inputType = fieldType === FIELD_TYPES.PASSWORD
      ? 'password'
      : fieldType === FIELD_TYPES.NUMBER
        ? 'number'
        : 'text';

    return (
      <TextField
        fullWidth
        type={inputType}
        multiline={multiline}
        rows={rows}
        label={label}
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(event) => handleFieldChange(key, event.target.value)}
        helperText={helperText}
        required={required}
        size="small"
      />
    );
  };

  if (!pluginAction?.type) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          {t('workflow.plugin.missing_definition', 'Plugin definition is missing.')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ActionSettingsLayout
        title={pluginAction.name || pluginAction.type}
        isEnabled={isEnabled}
        onEnabledChange={setIsEnabled}
        isSaveDisabled={isSaveDisabled}
        onSave={handleSave}
        onForceRun={handleForceRun}
        forceRunLoading={forceRunLoading}
        forceRunResult={forceRunResult}
        popUpOpen={popUpOpen}
        onCloseStatusWindow={handleCloseStatusWindow}
        forceRunWarningOpen={forceRunWarningOpen}
        forceRunWarningCount={forceRunWarningCount}
        onForceRunConfirm={handleForceRunConfirm}
        onForceRunCancel={handleForceRunCancel}
        conditions={conditions}
        onConditionsChange={handleConditionsChange}
        tags={tags}
        enableConditions={enableConditions}
      >
        <Grid item xs={12}>
          <Box sx={{ mb: 2 }}>
            {pluginLoadFailed ? (
              <Alert severity="error">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('workflow.plugin.load_failed.title', 'Failed to load this plugin')}
                </Typography>
                <Typography variant="body2">
                  {pluginLoadError || t('workflow.plugin.load_failed.message', 'The backend could not load this plugin module.')}
                </Typography>
                {pluginLogsPath && (
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    {t('workflow.plugin.load_failed.logs_path', 'See detailed logs in {{path}}', { path: pluginLogsPath })}
                  </Typography>
                )}
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {pluginAction.description || t('workflow.plugin.default_description', 'Configure this plugin action.')}
              </Typography>
            )}
            {resolvedPluginGuideUrl && (
              <Box sx={{ mt: 1.25 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleOpenPluginGuide}
                >
                  {t('workflow.plugin.open_guide', 'Open Guide')}
                </Button>
              </Box>
            )}
          </Box>
        </Grid>
        {!pluginLoadFailed && oauthConfig && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="subtitle1">
                    {t('workflow.plugin.oauth.title', '{{provider}} connection', { provider: oauthConfig.label })}
                  </Typography>
                  <Chip
                    size="small"
                    color={oauthConnectionStatus === 'connected' ? 'success' : oauthConnectionStatus === 'pending' ? 'warning' : 'default'}
                    label={oauthStatusLabel}
                  />
                </Box>

                {(!oauthConfig.accountField || !hasOAuthAccountField) && oauthConfig.accountRequired && (
                  <TextField
                    fullWidth
                    size="small"
                    label={t('workflow.plugin.oauth.account', '{{provider}} account', { provider: oauthConfig.label })}
                    value={oauthAccount}
                    onChange={(event) => setOauthAccount(event.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                )}

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: oauthMessage || oauthConfig.helperText ? 1.5 : 0 }}>
                  <Button
                    variant="contained"
                    onClick={handleOAuthConnect}
                    disabled={oauthIsAuthorizing || oauthIsChecking || oauthConnectionStatus === 'connected'}
                  >
                    {oauthConfig.connectLabel || t('workflow.plugin.oauth.connect', 'Connect {{provider}}', { provider: oauthConfig.label })}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleOAuthDisconnect}
                    disabled={oauthConnectionStatus === 'disconnected'}
                  >
                    {oauthConfig.disconnectLabel || t('workflow.plugin.oauth.disconnect', 'Disconnect')}
                  </Button>
                </Box>

                {oauthMessage && (
                  <Alert severity={oauthAlertSeverity} sx={{ mb: oauthConfig.helperText ? 1 : 0 }}>
                    {oauthMessage}
                  </Alert>
                )}

                {oauthConfig.helperText && (
                  <Typography variant="caption" color="text.secondary">
                    {oauthConfig.helperText}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        {!pluginLoadFailed && fields.map((field) => (
          <Grid item xs={12} key={field.key}>
            {renderField(field)}
          </Grid>
        ))}
        {!pluginLoadFailed && FolderBrowserComponent}
      </ActionSettingsLayout>
      <Dialog
        open={Boolean(resolvedPluginGuideUrl && isGuideDialogOpen)}
        onClose={handleClosePluginGuide}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, md: 2.5 },
            height: { xs: '100vh', md: '88vh' },
            m: { xs: 0, md: 3 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            py: 1.25,
            px: 2
          }}
        >
          <Typography component="div" variant="subtitle1" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pluginAction?.name || pluginAction?.type || t('workflow.plugin.open_guide', 'Open Guide')}
          </Typography>
          <IconButton onClick={handleClosePluginGuide} size="small" aria-label={t('common.close', 'Close')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ "--dialog-content-top-padding": "0px", p: 0, flex: 1, minHeight: 0, overflow: 'hidden', backgroundColor: '#fff' }}>
          {resolvedPluginGuideUrl ? (
            <iframe
              key={resolvedPluginGuideUrl}
              src={resolvedPluginGuideUrl}
              title={`${pluginAction?.name || pluginAction?.type || 'Plugin'} guide`}
              style={{
                width: '100%',
                height: '100%',
                border: 0,
                display: 'block',
                backgroundColor: '#fff'
              }}
            />
          ) : (
            <Box sx={{ p: 2.5 }}>
              <Typography color="error" variant="body2">
                {t('workflow.plugin.open_guide', 'Guide URL is missing.')}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default React.memo(ActionsPluginGeneric);
